'use client'

import React, { useState, useCallback, useEffect } from 'react'
import Link from 'next/link'
import clsx from 'clsx'
import { useSelector, useDispatch } from 'react-redux'
import { AppDispatch } from '@/store'
import { selectCurrentUser } from '@/store/user/slice'
import { setMemberInfo, selectMemberInfo } from '@/store/member/slice'
import { MembersService } from '@/lib/api/services/members'
import { getNotifications } from '@/store/notification/slice'
import { useAuth } from '@/hooks/useAuth'
import HeaderLogo from './HeaderLogo'
import HeaderNavigationMenu from './HeaderNavigationMenu'
import SideNav from '@/components/SideNav'
import SearchInputSuggestions from '@/components/Shared/SearchInputSuggestions'
import styles from './styles.module.scss'

export default function Header() {
  const dispatch = useDispatch<AppDispatch>()
  const { isAuthenticated } = useAuth()
  const currentUser = useSelector(selectCurrentUser)
  const memberInfo = useSelector(selectMemberInfo)
  const [showSearch, setShowSearch] = useState(false)
  const [isOpenSPMenu, setIsOpenSPMenu] = useState(false)
  const [scrollPosition, setScrollPosition] = useState(0)
  // Dashbord(マイ本棚やfollowのData)の読み込み
  const loadMemberData = useCallback(async () => {
    if (!isAuthenticated || !currentUser?.id) return
    if (memberInfo && memberInfo.memberId === currentUser.id) return

    try {
      const dashboard = await MembersService.getMemberDashboard(currentUser.id.toString())
      dispatch(
        setMemberInfo({
          memberId: currentUser.id,
          ...dashboard,
        }),
      )
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Dashbordの取得Error:', error)
    }
  }, [isAuthenticated, currentUser?.id, memberInfo, dispatch])

  useEffect(() => {
    if (isAuthenticated && currentUser?.id) {
      loadMemberData()
    }
  }, [isAuthenticated, currentUser?.id, loadMemberData])

  useEffect(() => {
    if (isAuthenticated && currentUser?.id) {
      dispatch(getNotifications(currentUser.id))
    }
  }, [isAuthenticated, currentUser?.id, dispatch])

  // モバイルメニューが開いている時のスクロール制御
  useEffect(() => {
    if (isOpenSPMenu) {
      document.body.style.top = `-${scrollPosition}px`
      document.body.classList.add('nonScroll')
    } else {
      document.body.style.top = ''
      document.body.classList.remove('nonScroll')
      if (scrollPosition > 0) {
        window.scrollTo(0, scrollPosition)
      }
    }

    return () => {
      document.body.style.top = ''
      document.body.classList.remove('nonScroll')
    }
  }, [isOpenSPMenu, scrollPosition])

  const handleCloseMenu = useCallback(() => {
    setIsOpenSPMenu(false)
  }, [])

  const handleToggleMenu = useCallback(() => {
    if (!isOpenSPMenu) {
      setScrollPosition(window.scrollY)
    }
    setIsOpenSPMenu(!isOpenSPMenu)
  }, [isOpenSPMenu])

  return (
    <>
      <header className={styles.header}>
        <div className={styles.headerContainer}>
          {/* SP: menu */}
          <button
            type='button'
            className={styles.spHamburger}
            onClick={handleToggleMenu}
            aria-label='メニューを開く'
            aria-expanded={isOpenSPMenu}
          >
            <div className={clsx(styles.hamburgerButton, { [styles.active]: isOpenSPMenu })}>
              <span />
              <span />
              <span />
            </div>
          </button>

          {/* left Group */}
          <div className={styles.leftGroup}>
            <HeaderLogo />
            <div className={styles.pcSearch}>
              <SearchInputSuggestions used='headerSuggestion' />
            </div>
          </div>

          {/* right Group */}
          <div className={styles.rightGroup}>
            {/* 共通 */}
            <Link href='/companies' className={clsx(styles.headerIconLink, styles.pcOnly)}>
              <img
                className={styles.headerIconImg}
                src='/images/svg/icon_search_company.svg'
                alt='企業検索'
                width={24}
                height={23}
              />
              <span className={styles.headerIconText}>企業検索</span>
            </Link>

            {isAuthenticated ? (
              <>
                {/* login後 */}
                <Link href='/members/mypage' className={clsx(styles.headerIconLink, styles.pcOnly)}>
                  <img
                    className={styles.headerIconImg}
                    src='/images/svg/icon_mypage.svg'
                    alt='マイページ'
                    width={21}
                    height={23}
                  />
                  <span className={styles.headerIconText}>マイページ</span>
                </Link>
                {/* menu */}
                <HeaderNavigationMenu transition='slide-y-transition' />
              </>
            ) : (
              /* PC: 未ログイン */
              <Link href='/login' className={clsx(styles.loginButton, styles.pcOnly)}>
                ログイン
              </Link>
            )}

            {/* SP: search icon */}
            <button
              type='button'
              className={styles.spSearchButton}
              onClick={() => setShowSearch(!showSearch)}
              aria-label='検索'
            >
              <i className={clsx('icon-font-icon_search', styles.spSearchIcon)} />
            </button>
          </div>
        </div>
      </header>

      {/* SP: search overlay */}
      {showSearch && (
        <div className={styles.spSearchOverlay}>
          <SearchInputSuggestions used='headerSuggestion' />
          <button
            type='button'
            className={styles.closeButton}
            onClick={() => setShowSearch(false)}
            aria-label='検索を閉じる'
          >
            閉じる
          </button>
        </div>
      )}

      {/* モバイルメニュー */}
      <div className={clsx(styles.spMenuBox, { [styles.open]: isOpenSPMenu })}>
        {isOpenSPMenu && <SideNav onCloseMenu={handleCloseMenu} shouldLoadData={true} />}
      </div>
    </>
  )
}
